import is from '@sindresorhus/is';
import type { ReactNode } from 'react';
import { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';
import type {
    JobArchitectureSummary as JobArchitecture,
    JobArchitectureItemStatistics,
} from '~/shared/data-contract/job-architecture';
import { Button } from '~/ui/core/button/Button';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import type { JobArchitectureStatistics } from '~/ui/domain/job-architecture-select/JobArchitectureSelect';
import { JobArchitectureSelect as JobArchitectureSelectComponent } from '~/ui/domain/job-architecture-select/JobArchitectureSelect';

import { useCreateJobArchitecture } from '../api/use-create-job-architecture';
import { useJobArchitecture } from '../api/use-job-architecture';
import { useJobArchitectureStatistics } from '../api/use-job-architecture-statistics';
import { useJobArchitectures } from '../api/use-job-architectures';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { getSetupEntryPath } from '../setup-dialog/get-setup-entry-path';
import { ArchitectureStatsRow } from './ArchitectureStatsRow';
import { useArchitectureSelectLabels } from './use-architecture-select-labels';

const localizeArchitectureTitle = (architecture: JobArchitecture, language: string): JobArchitecture => {
    const translation = architecture.translations.find((item) => item.language === language);

    return translation === undefined ? architecture : { ...architecture, title: translation.title };
};

export const JobArchitectureSelect = memo(
    ({
        activeJobArchitectureId,
        hideStatistics,
        onSwitchArchitecture,
        size,
        triggerBackgroundColor,
    }: {
        readonly activeJobArchitectureId?: string;
        readonly hideStatistics?: boolean;
        readonly onSwitchArchitecture: (architecture: JobArchitecture) => void;
        readonly size?: 'l' | 'm';
        readonly triggerBackgroundColor?: string;
    }) => {
        const { data: architectures } = useJobArchitectures();
        const { data: currentArchitectureDetails } = useJobArchitecture(activeJobArchitectureId);
        const translate = useTranslate();
        const isJobArchitect = useHasRole('job_architect');
        const contentLanguage = useJobArchitectureContentLanguage();
        const selectLabels = useArchitectureSelectLabels();

        const [isOpen, setIsOpen] = useState(false);
        const [visibleIds, setVisibleIds] = useState<readonly string[]>([]);
        const statisticsQuery = useJobArchitectureStatistics(
            isOpen && !hideStatistics
                ? visibleIds.filter(
                      (id) => architectures?.find((architecture) => architecture.id === id)?.statistics === undefined,
                  )
                : [],
            architectures?.some(
                (architecture) =>
                    visibleIds.includes(architecture.id) && architecture.generationInfo?.inProgress === true,
            ),
        );
        const navigate = useNavigate();

        const { mutateAsync: createJobArchitecture } = useCreateJobArchitecture();

        const getStatsContent = (data: JobArchitectureStatistics | undefined): ReactNode => {
            if (data === undefined) return null;

            const hasHierarchyStats =
                typeof data.familiesCount === 'number' &&
                typeof data.clustersCount === 'number' &&
                typeof data.rolesCount === 'number' &&
                typeof data.levelsCount === 'number';

            if (hasHierarchyStats) {
                return <ArchitectureStatsRow statistics={data as JobArchitectureItemStatistics} />;
            }

            return null;
        };

        const localizedArchitectures = useMemo(
            () => architectures?.map((architecture) => localizeArchitectureTitle(architecture, contentLanguage)),
            [architectures, contentLanguage],
        );
        const currentArchitecture = is.undefined(currentArchitectureDetails)
            ? localizedArchitectures?.find((architecture) => architecture.id === activeJobArchitectureId)
            : localizeArchitectureTitle(currentArchitectureDetails, contentLanguage);

        const handleCreateNewArchitecture = async (): Promise<void> => {
            const newArchitecture = await createJobArchitecture();
            setIsOpen(false);
            navigate(getSetupEntryPath(newArchitecture.id));
        };

        return (
            <JobArchitectureSelectComponent
                architectures={localizedArchitectures ?? []}
                ariaLabel={translate('pages.job-architecture-v2.select-architecture.placeholder')}
                createNewArchitecture={translate('pages.job-architecture.new-job-architecture')}
                currentArchitecture={currentArchitecture}
                data-testid="job-architecture-select"
                getItemStatsContent={(id) => {
                    const statistics =
                        statisticsQuery.data?.[id] ??
                        architectures?.find((architecture) => architecture.id === id)?.statistics;
                    if (statistics !== undefined) return <ArchitectureStatsRow statistics={statistics} />;
                    if (statisticsQuery.isError)
                        return (
                            <Button
                                variant="link"
                                size="sm"
                                onKeyDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void statisticsQuery.refetch();
                                }}
                            >
                                {translate('errors.common.action')}
                            </Button>
                        );
                    if (statisticsQuery.isSuccess) return <span>—</span>;
                    return (
                        <span role="status" aria-label={translate('common.loading')}>
                            <Skeleton className="h-4 w-40" />
                        </span>
                    );
                }}
                getStatsContent={getStatsContent}
                hideStatistics={hideStatistics}
                labels={selectLabels}
                open={isOpen}
                placeholder={translate('pages.job-architecture-v2.select-architecture.placeholder')}
                size={size}
                triggerBackgroundColor={triggerBackgroundColor}
                onCreateNewArchitecture={isJobArchitect ? handleCreateNewArchitecture : undefined}
                onOpenChange={setIsOpen}
                onVisibleArchitectureIdsChange={setVisibleIds}
                onSwitchArchitecture={(id) => {
                    const architecture = architectures?.find((arch) => arch.id === id);
                    if (is.undefined(architecture)) {
                        return;
                    }

                    onSwitchArchitecture(architecture);
                }}
            />
        );
    },
);
